import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { FrequencySettingsFields } from './FrequencySettingsFields';
import { ProductSourceCard } from './ProductSourceCard';
import { ProductSourceModal } from '../ProductSourceModal';
import type { Product } from '../ProductSourceModal';
import type { PackRelease, ReleaseDiscountType } from '../PackSizesPanel';
import { formatReleaseTitle } from '../../utils/releases';
interface ReleaseCardProps {
  idPrefix: string;
  release: PackRelease;
  // The pack's Product Source; each release picks from these
  packProducts: Product[];
  packMin: number;
  packMax: number;
  canDelete: boolean;
  customTiers: string[];
  onAddCustomTier: (tier: string) => void;
  onUpdate: (updates: Partial<PackRelease>) => void;
  onDelete: () => void;
}
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = Array.from({
  length: 28
}, (_, index) => index + 1);
const DISCOUNT_TYPES: {
  id: ReleaseDiscountType;
  symbol: string;
  label: string;
}[] = [{
  id: 'percentage',
  symbol: '%',
  label: 'Percentage'
}, {
  id: 'flat',
  symbol: '$',
  label: 'Flat price'
}];
const selectClass = 'w-full h-[36px] pl-3 pr-8 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer';
const inputClass = 'w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20';
function formatDiscountBadge(release: PackRelease): string | null {
  if (release.discountType === 'flat') return release.flatPrice ? `$${release.flatPrice}` : null;
  return release.discount ? `${release.discount}%` : null;
}

// One release on a pack billed on fixed release dates: its own date, pricing, products and settings
export function ReleaseCard({
  idPrefix,
  release,
  packProducts,
  packMin,
  packMax,
  canDelete,
  customTiers,
  onAddCustomTier,
  onUpdate,
  onDelete
}: ReleaseCardProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const isCollapsed = release.isRowExpanded === false;
  const toggle = () => onUpdate({
    isRowExpanded: isCollapsed
  });
  const fieldId = `${idPrefix}-${release.id}`;
  const title = formatReleaseTitle(release);
  const name = release.name.trim();
  const productCount = Object.keys(release.products).filter(id => packProducts.some(p => p.id === id)).length;
  const badge = formatDiscountBadge(release);
  const deleteLabel = canDelete ? `Delete ${title}` : 'A pack on fixed release dates needs at least one release';
  if (isCollapsed) {
    return <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-4082" data-mp="wjbvnibhrwal">
        <div role="button" tabIndex={0} aria-expanded={false} onClick={toggle} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }} className="flex items-center justify-between px-4 py-3 bg-white border-l-[3px] border-l-[#10B981] hover:bg-[#F8FAFC] transition-colors cursor-pointer" data-id="element-4083" data-mp="rks6za5ik6ej">
          <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap" data-id="element-4084" data-mp="2z7bt9kl881l9">
            <div className="w-2 h-2 rounded-full bg-[#10B981] flex-shrink-0" data-id="element-4085" data-mp="2z57eu8ppbk6q" />
            <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-4086" data-mp="3mxxouce1nted">
              {title}
            </span>
            {name && <span className="text-[13px] text-[#64748B]" data-id="element-4087" data-mp="3qweyp3ounhzh">{name}</span>}
            {badge && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold bg-[#D1FAE5] text-[#059669]" data-id="element-4088" data-mp="3cyqdfuqve8tw">
                {badge}
              </span>}
            <span className="text-[13px] text-[#94A3B8]" data-id="element-4089" data-mp="21j1leoyrejm8">{productCount} products</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4090" data-mp="26khbivfsngbh">
            <button type="button" disabled={!canDelete} title={deleteLabel} aria-label={deleteLabel} onClick={e => {
            e.stopPropagation();
            onDelete();
          }} className="p-1.5 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#94A3B8]" data-id="element-4091" data-mp="2lst3mh8gsp5u">
              <Trash2 className="w-4 h-4" data-id="element-4092" data-mp="34rx9jh4pm2ta" />
            </button>
            <ChevronDown className="w-4 h-4 text-[#94A3B8] ml-1" data-id="element-4093" data-mp="34p2r1v59as2i" />
          </div>
        </div>
      </div>;
  }
  return <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-4094" data-mp="2vqqtta9vww13">
      <ProductSourceModal isOpen={isPickerOpen} onClose={() => setIsPickerOpen(false)} onConfirm={(selectedIds, quantities) => {
      const products = selectedIds.reduce<Record<string, number>>((chosen, id) => {
        chosen[id] = quantities?.[id] ?? 1;
        return chosen;
      }, {});
      onUpdate({
        products
      });
      setIsPickerOpen(false);
    }} mode="curated" packMin={packMin} packMax={packMax} products={packProducts} initialSelectedIds={Object.keys(release.products)} initialQuantities={release.products} data-id="element-4095" data-mp="2vstr8fdd9rfu" />
      <div className="bg-[#F0F9FF] border-l-[3px] border-l-[#0EA5E9]" data-id="element-4096" data-mp="1f66mfpz4464g">
        <div className="flex items-center justify-between px-4 py-2 bg-[#E0F2FE] border-b border-[#BAE6FD]" data-id="element-4097" data-mp="1f4vjfsdhr2ee">
          <div className="flex items-center gap-2 min-w-0" data-id="element-4098" data-mp="1nz1t8o48a9wc">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-[#0EA5E9] text-white uppercase tracking-wide flex-shrink-0" data-id="element-4099" data-mp="1nys4t4yu0asr">
              Editing
            </span>
            <span className="text-[13px] font-semibold text-[#0369A1] truncate" data-id="element-4100" data-mp="1xxzkhyt1ghj1">
              {title}
              {name && <span className="font-normal" data-id="element-4101" data-mp="22tycrxhhmjoq"> · {name}</span>}
            </span>
          </div>
          <button type="button" aria-expanded={true} className="flex items-center gap-1 text-[12px] font-medium text-[#0369A1] hover:text-[#0F172A] hover:bg-[#BAE6FD] px-2 py-1 rounded transition-colors" onClick={toggle} data-id="element-4102" data-mp="3l9pteuuj4al">
            <ChevronUp className="w-3.5 h-3.5" data-id="element-4103" data-mp="3ro4wii6u9que" />
            Collapse
          </button>
        </div>

        <div className="px-4 py-4 bg-white space-y-5" data-id="element-4104" data-mp="afhzk2lbr9xw">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4105" data-mp="ah2tbpzteoup">
            <div data-id="element-4106" data-mp="5gf0cys0zxxu">
              <span className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4107" data-mp="5fvh9eqn33i8">
                Release date
              </span>
              <div className="flex items-center gap-2" data-id="element-4108" data-mp="je3elnrl86tx">
                <div className="relative flex-1" data-id="element-4109" data-mp="eglcag82spuw">
                  <select value={release.month} onChange={e => onUpdate({
                  month: Number(e.target.value)
                })} aria-label="Release month" className={selectClass} data-id="element-4110" data-mp="t69dlhg9boim">
                    {MONTHS.map((month, index) => <option key={month} value={index + 1} data-id="element-4111" data-mp="1265xz6vm617v" data-mpi={index}>
                        {month}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-4112" data-mp="y39ab1r7vex2" />
                </div>
                <div className="relative w-[96px]" data-id="element-4113" data-mp="1lszagorkeqzq">
                  <select value={release.day} onChange={e => onUpdate({
                  day: Number(e.target.value)
                })} aria-label="Release day" className={selectClass} data-id="element-4114" data-mp="1lqd7l2fmcznu">
                    {DAYS.map(day => <option key={day} value={day} data-id="element-4115" data-mp="1ctvqzjohk9kp">
                        {day}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-4116" data-mp="12uwut0kwq6el" />
                </div>
              </div>
            </div>
            <div data-id="element-4117" data-mp="t3absi2bv31a">
              <label htmlFor={`${fieldId}-name`} className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4118" data-mp="t4v564cwofxk">
                Release name{' '}
                <span className="font-normal text-[#94A3B8]" data-id="element-4119" data-mp="k3ds6uusoud3">(optional)</span>
              </label>
              <input id={`${fieldId}-name`} type="text" value={release.name} onChange={e => onUpdate({
              name: e.target.value
            })} placeholder="e.g. Autumn/Winter" className={inputClass} data-id="element-4120" data-mp="zlervtvet5u7" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4121" data-mp="qjnl0lp4usyr">
            <div data-id="element-4122" data-mp="llvv1x81tgx7">
              <span className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4123" data-mp="lmf38m7wcqy1">
                Discount
              </span>
              {release.discountType === 'percentage' ? <input type="number" min="0" max="100" value={release.discount} onChange={e => onUpdate({
              discount: e.target.value
            })} placeholder="e.g. 10" aria-label="Discount percentage" className={inputClass} data-id="element-4124" data-mp="bvsrx477qml5" /> : <input type="number" min="0" step="0.01" value={release.flatPrice} onChange={e => onUpdate({
              flatPrice: e.target.value
            })} placeholder="e.g. 477" aria-label="Flat price" className={inputClass} data-id="element-4125" data-mp="2vxfyx5u2nk2" />}
            </div>
            <div data-id="element-4126" data-mp="3p4ouz77wm78w">
              <span className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4127" data-mp="24kk2kxki2diy">
                Discount Type
              </span>
              <div className="flex items-center gap-2" data-id="element-4128" data-mp="29ibqlgi0xfhv">
                {DISCOUNT_TYPES.map(type => {
                const isSelected = release.discountType === type.id;
                return <button key={type.id} type="button" aria-pressed={isSelected} onClick={() => onUpdate({
                  discountType: type.id
                })} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md border text-[13px] font-medium transition-colors ${isSelected ? 'bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]' : 'bg-white border-transparent text-[#94A3B8] hover:text-[#475569]'}`} data-id="element-4129" data-mp="2ikl0c45gx4u7">
                      <span className={`text-[15px] font-semibold ${isSelected ? 'text-[#0F172A]' : ''}`} data-id="element-4130" data-mp="1zm8qeg6qfqxk">
                        {type.symbol}
                      </span>
                      {type.label}
                    </button>;
              })}
              </div>
            </div>
          </div>

          <div data-id="element-4131" data-mp="1uid4falq7zmb">
            <h4 className="text-[13px] font-semibold text-[#0F172A] mb-2" data-id="element-4132" data-mp="1uivaeyy6xs7f">
              Product Source
            </h4>
            <ProductSourceCard collectionName={productCount > 0 ? 'Manual Selection' : 'No products selected'} productCount={productCount} onChange={() => setIsPickerOpen(true)} data-id="element-4133" data-mp="1zk2hswwf8pdk" />
            <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4134" data-mp="28i4q7xihikav">
              Chosen from this pack's Product Source
            </p>
          </div>
        </div>

        <div className="border-t border-[#E2E8F0] bg-[#FAFAFA]" data-id="element-4135" data-mp="1fth1fryj1csk">
          <div className="px-4 pt-4" data-id="element-4136" data-mp="1ktbr5s6kfwc1">
            <FrequencySettingsFields cssHandle={`release-${release.month}-${release.day}`} storefrontHeading="Storefront Card Content For This Release" loyaltyTier={release.loyaltyTier} adHocDiscount={release.adHocDiscount} pausedDiscount={release.pausedDiscount} customTiers={customTiers} onAddCustomTier={onAddCustomTier} onChange={onUpdate} data-id="element-4137" data-mp="1ksafblhq75t9" />
          </div>
          <div className="flex items-center px-4 py-3 mt-2 border-t border-[#E2E8F0]" data-id="element-4138" data-mp="pk845f5x251o">
            <button type="button" disabled={!canDelete} title={deleteLabel} onClick={onDelete} className="flex items-center gap-1.5 text-[13px] font-medium text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2] px-2 py-1.5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" data-id="element-4139" data-mp="phvqvfb3siuy">
              <Trash2 className="w-3.5 h-3.5" data-id="element-4140" data-mp="6lvxc02h9799" />
              Delete Release
            </button>
          </div>
        </div>
      </div>
    </div>;
}